import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useOutletContext } from "react-router-dom";
import AdSenseUnit from "../components/AdSenseUnit.jsx";
import QuizCard from "../components/QuizCard.jsx";
import { fetchAllQuizzes } from "../api.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/v2/api";
const PAGE_SIZE = 25;

async function fetchPage(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to load quizzes");
  const data = await res.json();
  return {
    items: Array.isArray(data) ? data : (data.results || []),
    next: Array.isArray(data) ? null : (data.next || null),
  };
}

const Home = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [status, setStatus] = useState("loading");
  const [nextUrl, setNextUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const outletContext = useOutletContext();
  const setAvailableCategories = outletContext?.setAvailableCategories;
  const sentinelRef = useRef(null);

  // Ref so the IntersectionObserver callback always sees current state
  // without needing to be recreated on every render.
  const stateRef = useRef({ nextUrl: null, loadingMore: false });
  stateRef.current = { nextUrl, loadingMore };

  // Initial page load
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setStatus("loading");
        const { items, next } = await fetchPage(
          `${API_BASE}/quizzes/?page_size=${PAGE_SIZE}`
        );
        if (cancelled) return;
        setQuizzes(items);
        setNextUrl(next);
        const cats = Array.from(
          new Set(items.map((q) => q.category).filter(Boolean))
        ).sort();
        setAvailableCategories?.(cats);
        setStatus("success");
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    load();
    return () => { cancelled = true; };
  }, [setAvailableCategories]);

  // Kept in a ref so the observer callback never captures a stale closure
  const loadMoreRef = useRef(null);
  loadMoreRef.current = async () => {
    const { nextUrl: url, loadingMore: busy } = stateRef.current;
    if (!url || busy) return;
    setLoadingMore(true);
    try {
      const { items, next } = await fetchPage(url);
      setQuizzes((prev) => [...prev, ...items]);
      setNextUrl(next);
      if (setAvailableCategories) {
        setAvailableCategories((prev) => {
          const merged = new Set([...(prev || []), ...items.map((q) => q.category).filter(Boolean)]);
          return Array.from(merged).sort();
        });
      }
    } catch {
      // Silent — user can scroll again to retry
    } finally {
      setLoadingMore(false);
    }
  };

  // Populate the full category nav independently of the paginated quiz list,
  // so categories that only appear on later pages still show up in the top
  // nav for users who never scroll. Runs once on mount and replaces whatever
  // partial set was derived from the first paginated batch.
  useEffect(() => {
    if (!setAvailableCategories) return;
    let cancelled = false;
    fetchAllQuizzes()
      .then((all) => {
        if (cancelled) return;
        const cats = Array.from(
          new Set(all.map((q) => q.category).filter(Boolean))
        ).sort();
        setAvailableCategories(cats);
      })
      .catch(() => {
        // Silent — the first paginated batch already populated a partial list
      });
    return () => { cancelled = true; };
  }, [setAvailableCategories]);

  // Wire up IntersectionObserver on the sentinel element once it is mounted.
  // status is included in deps so the effect re-runs after the "loading" →
  // "success" transition renders the sentinel into the DOM.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current?.();
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [status]);

  if (status === "loading") {
    return (
      <div className="w-full space-y-10">
        <Hero />
        <AdSenseUnit />
        <div className="grid gap-6 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-48 animate-pulse rounded-2xl bg-white/5" />
          ))}
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="w-full space-y-10">
        <Hero />
        <AdSenseUnit />
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center text-red-200">
          Something went wrong while loading quizzes. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-10">
      <Helmet>
        <title>QuizFreak — Free Online Quizzes | Test Your Knowledge</title>
        <meta name="description" content="Take fun, shareable quizzes on food, TV, geography, pop culture, and more. See how you stack up against everyone else." />
        <link rel="canonical" href="https://quizfreak.com/v2/" />
      </Helmet>
      <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-brand-glow/20 via-purple-500/20 to-pink-500/20 p-8 text-white">
        <p className="text-sm uppercase tracking-[0.3em] text-white/70">
          QuizFreak
        </p>
        <h1 className="mt-3 text-4xl font-semibold leading-tight">
          Prove you're the sharpest in your group chat.
        </h1>
        <p className="mt-3 max-w-2xl text-lg text-white/80">
          Fast quizzes built for mobile thumbs, not desktop nostalgia. We're rolling out a modern experience that keeps the legacy site safe while we experiment.
        </p>
      </div>
      <AdSenseUnit />

      {quizzes.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/70">
          No quizzes are available yet. Check back soon.
        </div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2">
            {quizzes.map((quiz) => (
              <QuizCard key={quiz.id} quiz={quiz} />
            ))}
          </div>

          {/* Sentinel — observed to trigger the next page load */}
          <div ref={sentinelRef} className="h-4" />

          {loadingMore && (
            <div className="grid gap-6 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-48 animate-pulse rounded-2xl bg-white/5" />
              ))}
            </div>
          )}

          {!nextUrl && !loadingMore && quizzes.length > 0 && (
            <p className="text-center text-sm text-white/30">You've seen all the quizzes.</p>
          )}
        </>
      )}
    </div>
  );
};

export default Home;

function Hero() {
  return (
    <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-brand-glow/20 via-purple-500/20 to-pink-500/20 p-8 text-white">
      <p className="text-sm uppercase tracking-[0.3em] text-white/70">QuizFreak</p>
      <h1 className="mt-3 text-4xl font-semibold leading-tight">
        Prove you're the sharpest in your group chat.
      </h1>
      <p className="mt-3 max-w-2xl text-lg text-white/80">
        Fast quizzes built for mobile thumbs, not desktop nostalgia. We're rolling out a modern
        experience that keeps the legacy site safe while we experiment.
      </p>
    </div>
  );
}
